<template>
  <div v-if="isEnabled">
    <div v-if="showForm" class="opportunity-form">
      <div v-if="!addingNewProject">
        <div class="form-group col-lg-4 p-0">
          <label for="existingProject" class="form-label">
            {{ $resourceString["checkout.SFDC.select-opportunity"] }}
          </label>
          <select id="existingProject" v-model="selectedUserProject" class="form-select custom-select">
            <option disabled value="">
              {{ $resourceString["checkout.SFDC.existing-projects"] }}
            </option>
            <option v-for="option in existingProjectOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
        <div class="form-group d-flex flex-column col-lg-4 p-0 pb-3">
          <span>
            {{ $resourceString["checkout.SFDC.no-projects"] }}
          </span>
          <a href="#" @click="toggleAddProjectSection">
            {{ $resourceString["checkout.SFDC.add-new"] }}
          </a>
        </div>
      </div>

      <div v-if="addingNewProject">
        <div class="form-group col-lg-4 p-0">
          <label for="newOpportunityName" class="form-label">
            {{ $resourceString["checkout.SFDC.enter-name"] }}
          </label>
          <input id="newOpportunityName" type="text" v-model="name" class="form-input"
            :placeholder="$resourceString['checkout.SFDC.project-name-placeholder']" 
          />
        </div>

        <div class="form-group col-lg-4 p-0">
          <label for="opportunityType" class="form-label">{{ $resourceString["checkout.SFDC.project-type"] }}</label>
          <select id="opportunityType" v-model="selectedType" class="form-select custom-select">
            <option disabled value="">
              {{ $resourceString["checkout.SFDC.select-project-type"] }}
            </option>
            <option v-for="option in typeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group col-lg-4 p-0" v-if="selectedType">
          <label for="opportunity" class="form-label">{{ $resourceString["checkout.SFDC.segment"] }}</label>
          <select id="opportunity" v-model="selectedProject" class="form-select custom-select"
            @change="handleProjectChange">
            <option disabled value="">
              {{ $resourceString["checkout.SFDC.select-segment-type"] }}
            </option>
            <option v-for="option in projectOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group col-lg-4 p-0" v-if="primaryDPropOptions.length > 0">
          <label :for="primaryDynamicKey" class="form-label">{{ formatDynamicLabel(primaryDynamicKey) }}</label>
          <select :id="primaryDynamicKey" v-model="dynamicProperties[primaryDynamicKey]"
            @change="handlePrimaryDPropChange" class="form-select custom-select">
            <option disabled value="">
              {{ $resourceString["checkout.SFDC.select-option"] }}
            </option>
            <option v-for="option in primaryDPropOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="form-group col-lg-4 p-0" v-if="secondaryDPropOptions.length > 0">
          <label :for="secondaryDynamicKey" class="form-label">{{ formatDynamicLabel(secondaryDynamicKey) }}</label>
          <select :id="secondaryDynamicKey" v-model="dynamicProperties[secondaryDynamicKey]"
            @change="handleSecondaryDPropChange" class="form-select custom-select">
            <option disabled value="">{{ $resourceString["checkout.SFDC.select-option"] }}</option>
            <option v-for="option in secondaryDPropOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>

        <div class="pb-3">
          <a href="#" @click="toggleAddProjectSection">
            {{ $resourceString["checkout.SFDC.existing-projects"] }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import PatcraftApi from '../../../scripts/patcraft-api';

export default {
  props: {
    userEmail: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      id: '',
      isEnabled: false,
      selectedUserProject: '',
      selectedType: '',
      selectedProject: '',
      accountId: '',
      name: '',
      addingNewProject: false,
      existingProjectOptions: [],
      picklistValues: null,
      opportunityError: '',
      contactData: {},
      showForm: false,
      dynamicProperties: {},
      primaryDynamicKey: '',
      secondaryDynamicKey: '',
      // DEV NOTE: Add opportunities to the opportunityDropdownMapping object with their respective dynamic dropdowns
      opportunityDropdownMapping: {
        Lodging: ['Hotel_Chain__c', 'Type__c'],
      },
    };
  },
  computed: {
    typeOptions() {
      return this.picklistValues
        ? this.picklistValues.picklistFieldValues.Type.values.map(option => ({
          label: option.label,
          value: option.value,
        }))
        : [];
    },
    projectOptions() {
      if (!this.selectedType || !this.picklistValues) return [];
      const controllerValue =
        this.picklistValues.picklistFieldValues.Project_Type__c.controllerValues[
        this.selectedType
        ];
      return this.picklistValues.picklistFieldValues.Project_Type__c.values
        .filter(option => option.validFor.includes(controllerValue))
        .map(option => ({
          label: option.label,
          value: option.value,
        }));
    },
    primaryDPropOptions() {
      if (!this.primaryDynamicKey || !this.picklistValues) return [];
      const field = this.picklistValues.picklistFieldValues[this.primaryDynamicKey];
      return field.values.map(option => ({
        label: option.label,
        value: option.value,
      }));
    },
    secondaryDPropOptions() {
      if (
        !this.secondaryDynamicKey ||
        !this.dynamicProperties[this.primaryDynamicKey] ||
        !this.picklistValues
      )
        return [];
      const field = this.picklistValues.picklistFieldValues[this.secondaryDynamicKey];
      const selectedPrimaryValue = this.dynamicProperties[this.primaryDynamicKey];
      const controllerValues = field.controllerValues;
      const matchedKeys = Object.keys(controllerValues).filter(key => key === selectedPrimaryValue);
      if (matchedKeys.length === 0) return [];
      return field.values
        .filter(option => option.validFor.includes(controllerValues[selectedPrimaryValue]))
        .map(option => ({
          label: option.label,
          value: option.value,
        }));
    },
    shouldShowDynamicDropdowns() {
      return (
        this.selectedProject &&
        this.opportunityDropdownMapping[this.selectedProject]
      );
    },
  },
  mounted() {
    this.isOpportunitiesEnabled().then(() => {
      if (this.isEnabled) {
        this.fetchPicklistOptions();
      }
    }).catch(error => {
      console.error("Error during mounted lifecycle:", error);
    });
  },
  watch: {
    userEmail: {
      immediate: true,
      handler(newEmail) {
        if (newEmail) {
          this.fetchContact(newEmail);
          this.fetchUserOpportunities(newEmail);
        }
      },
    },
    selectedType(newValue) {
      this.resetDynamicProperties();
      this.updateOpportunityData();
    },
    selectedProject(newValue) {
      this.handleProjectChange();
      this.updateOpportunityData();
    },
    dynamicProperties: {
      deep: true,
      handler() {
        this.updateOpportunityData();
      },
    },
    selectedUserProject(newValue) {
      if (!this.addingNewProject) {
        const selectedOpportunity = this.existingProjectOptions.find(
          (option) => option.value === newValue
        );

        if (selectedOpportunity) {
          this.updateOpportunityData();
          this.$emit('update-project-name', selectedOpportunity.label);
        }
      }
    },
    name(newValue) {
      this.updateOpportunityData();
      this.$emit('update-project-name', newValue);
    },
  },
  methods: {
    toggleAddProjectSection() {
      this.addingNewProject = !this.addingNewProject;
    },
    async isOpportunitiesEnabled() {
      try {
        const response = await PatcraftApi.isOpportunitiesEnabled();
        this.isEnabled = response.data;
        console.log(this.isEnabled);
      } catch (error) {
        console.error("Error checking if opportunities are enabled:", error);
        return false;
      }
    },
    async fetchUserOpportunities(userEmail) {
      if (!userEmail || userEmail === null) {
        this.opportunityError = "Email not available in address.";
        return;
      }
      try {
        const response = await PatcraftApi.getSalesforceOpportunities(userEmail, "patcraft");
        this.existingProjectOptions = response.data.map(opportunity => ({
          label: opportunity.Name,
          value: opportunity.Id,
        }));
      } catch (error) {
        console.error("Error fetching user opportunities:", error);
        this.opportunityError = "Failed to fetch user opportunities. Please try again.";
      }
    },
    async fetchContact(userEmail) {
      if (!userEmail) {
        this.opportunityError = "Email not available in address.";
        return;
      }
      try {
        const response = await PatcraftApi.getSalesforceContact(userEmail, "patcraft");
        this.contactData = response.data;
        if (this.contactData) {
          this.$emit('contact-found', true);
          this.showForm = true;
          this.accountId = this.contactData.accountId;
        } else {
          this.$emit('contact-found', false);
        }
      } catch (error) {
        console.error("Error fetching contact:", error);
        this.opportunityError = "Failed to fetch contact data. Please try again.";
        this.$emit('contact-found', false);
      }
    },
    async fetchPicklistOptions() {
      try {
        const response = await PatcraftApi.getSalesforceOpportunitiesPicklistValues();
        this.picklistValues = response.data;
      } catch (error) {
        console.error("Error fetching picklist options:", error);
      }
    },
    handleProjectChange() {
      this.$set(this.dynamicProperties, 'Project_Type__c', this.selectedProject);

      // Determine if the selected project type has specific dynamic dropdowns
      const dynamicFields = this.opportunityDropdownMapping[this.selectedProject] || [];

      if (dynamicFields.length > 0) {
        this.primaryDynamicKey = dynamicFields[0] || '';
        this.secondaryDynamicKey = dynamicFields[1] || '';

        if (this.primaryDynamicKey) {
          this.$set(this.dynamicProperties, this.primaryDynamicKey, '');
        }
        if (this.secondaryDynamicKey) {
          this.$set(this.dynamicProperties, this.secondaryDynamicKey, '');
        }
      } else {
        this.primaryDynamicKey = '';
        this.secondaryDynamicKey = '';
      }
    },
    handlePrimaryDPropChange() {
      const selectedValue = this.dynamicProperties[this.primaryDynamicKey];
      const field = this.picklistValues.picklistFieldValues[this.primaryDynamicKey];
      const controllerValue = field.controllerValues[selectedValue];

      this.secondaryDynamicKey = '';
      this.$delete(this.dynamicProperties, this.secondaryDynamicKey);

      const fieldKey = Object.keys(this.picklistValues.picklistFieldValues).find(k => {
        const field = this.picklistValues.picklistFieldValues[k];
        return field.controllerValues && field.controllerValues.hasOwnProperty(selectedValue);
      });

      if (fieldKey) {
        this.secondaryDynamicKey = fieldKey;
        this.$set(this.dynamicProperties, this.secondaryDynamicKey, '');
      }

      this.updateSecondaryDPropOptions();
    },
    updateSecondaryDPropOptions() {
      if (!this.secondaryDynamicKey || !this.dynamicProperties[this.primaryDynamicKey]) return;
      const selectedPrimaryValue = this.dynamicProperties[this.primaryDynamicKey];
      const field = this.picklistValues.picklistFieldValues[this.secondaryDynamicKey];
      const controllerValues = field.controllerValues;
      const matchedKeys = Object.keys(controllerValues).filter(key => key === selectedPrimaryValue);
      if (matchedKeys.length > 0) {
        this.secondaryDPropOptionsCache = field.values
          .filter(option => option.validFor.includes(controllerValues[selectedPrimaryValue]))
          .map(option => ({
            label: option.label,
            value: option.value,
          }));
      } else {
        this.secondaryDPropOptionsCache = [];
      }
    },
    handleSecondaryDPropChange() {
      this.updateOpportunityData();
    },
    resetDynamicProperties() {
      this.primaryDynamicKey = '';
      this.secondaryDynamicKey = '';
      this.dynamicProperties = {};
    },
    updateOpportunityData() {
      const isExistingOpportunitySelected = this.selectedUserProject && !this.addingNewProject;

      // Construct SalesforceOpportunity payload
      const SalesforceOpportunity = isExistingOpportunitySelected
        ? {
          Id: this.selectedUserProject, // Send the Id for an existing opportunity
          Name: this.existingProjectOptions.find(
            (option) => option.value === this.selectedUserProject
          )?.label || "", // Retrieve the name based on selectedUserProject
          AccountId: this.accountId, // Ensure AccountId is also included
        }
        : {
          Name: this.name,
          Type: this.selectedType,
          AccountId: this.accountId,
          DynamicProperties: this.dynamicProperties,
        };

      // Dispatch action to update the store with SalesforceOpportunity payload
      this.$store.dispatch('checkoutPage/updateOpportunityData', { SalesforceOpportunity });
    },
    formatDynamicLabel(key) {
      return key
        .replace(/__c$/, '')
        .replace(/_/g, ' ');
    },
  },
};
</script>

<style></style>
